import { useCallback } from 'react';
import { Group, ColumnConfig, GroupedRowData } from './types';
import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid-premium';

/**
 * Centralized function to extract row ID as string
 * Uses nullish coalescing to properly handle null/undefined
 * @param row - Row with id or _id field
 * @returns String representation of the row ID
 * @throws Error if no valid ID is found
 */
export const getRowIdAsString = <T extends { id?: string; _id?: string }>(
  row: T
): string => {
  const id = row._id ?? row.id;
  if (id === null || id === undefined || id === '') {
    throw new Error(
      `Row must have a valid id or _id field. Received: ${JSON.stringify(row)}`
    );
  }
  return String(id);
};

/**
 * Transform flat data into grouped format for MUI DataGrid tree data
 */
export const transformDataForGrouping = <
  T extends { id?: string; _id?: string },
>(
  data: T[],
  groups: Group[],
  isGroupingEnabled: boolean
): GroupedRowData[] => {
  if (!isGroupingEnabled || groups.length === 0) {
    return data.map(item => ({ ...item, isGroup: false }));
  }

  const result: GroupedRowData[] = [];

  // Create group rows and their children
  groups.forEach(group => {
    const groupItems = data.filter(item =>
      group.rowIds.includes(getRowIdAsString(item))
    );
    const groupItemsCount = groupItems.length;

    if (groupItemsCount > 0) {
      const groupNameWithCount = `${group.name} (${groupItemsCount})`;

      // Add group header row - use client_name to show the group title
      const groupRow: GroupedRowData = {
        id: `group-${group.id}`,
        _id: `group-${group.id}`,
        isGroup: true,
        groupName: groupNameWithCount,
        groupId: group.id,
      };

      // Fill all other columns with empty values to prevent data display in group rows
      const sampleData = data[0];
      if (sampleData) {
        Object.keys(sampleData).forEach(key => {
          if (
            key !== 'id' &&
            key !== '_id' &&
            key !== 'isGroup' &&
            key !== 'groupName' &&
            key !== 'groupId'
          ) {
            groupRow[key] = '';
          }
        });
      }

      result.push(groupRow);

      // Add children rows for this group
      groupItems.forEach(item => {
        result.push({
          ...item,
          isGroup: false,
          parentGroupId: `group-${group.id}`, // Use the full group row ID
          parentGroupName: group.name,
          parentGroupFullName: groupNameWithCount, // Store the full name with count for tree path
        });
      });
    }
  });

  // Add ungrouped items in an "Ungrouped" group
  const ungroupedItems = data.filter(
    item => !groups.some(group => group.rowIds.includes(getRowIdAsString(item)))
  );

  if (ungroupedItems.length > 0) {
    const ungroupedGroupName = `Ungrouped (${ungroupedItems.length})`;

    // Create "Ungrouped" group header
    const ungroupedGroup: GroupedRowData = {
      id: 'group-ungrouped',
      _id: 'group-ungrouped',
      isGroup: true,
      groupName: ungroupedGroupName,
      groupId: 'ungrouped',
    };

    // Fill all other columns with empty values to prevent data display in group rows
    const sampleData = data[0];
    if (sampleData) {
      Object.keys(sampleData).forEach(key => {
        if (
          key !== 'id' &&
          key !== '_id' &&
          key !== 'isGroup' &&
          key !== 'groupName' &&
          key !== 'groupId'
        ) {
          ungroupedGroup[key] = '';
        }
      });
    }

    result.push(ungroupedGroup);

    // Add ungrouped items as children of the "Ungrouped" group
    ungroupedItems.forEach(item => {
      result.push({
        ...item,
        isGroup: false,
        parentGroupId: 'group-ungrouped',
        parentGroupName: 'Ungrouped',
        parentGroupFullName: ungroupedGroupName,
      });
    });
  }

  return result;
};

/**
 * Get tree data path for MUI DataGrid grouping
 */
export const getTreeDataPath = (row: GroupedRowData): string[] => {
  // For group rows, they are at the root level of the tree
  if (row.isGroup) {
    return [getRowIdAsString(row)];
  }

  // For child rows that belong to a group, they are children of the group
  if (row.parentGroupId) {
    return [row.parentGroupId, getRowIdAsString(row)];
  }

  // For ungrouped rows, they are also at root level but separate from groups
  return [getRowIdAsString(row)];
};

/**
 * Check if a row has children (for group expansion)
 */
export const hasChildren = (row: GroupedRowData): boolean => {
  return row.isGroup === true;
};

/**
 * Get group summary information
 */
export const getGroupSummary = (
  groups: Group[],
  data: GroupedRowData[]
): Array<{
  groupId: string;
  groupName: string;
  itemCount: number;
}> => {
  return groups.map(group => ({
    groupId: group.id,
    groupName: group.name,
    itemCount: group.rowIds.filter(rowId =>
      data.some(item => item._id === rowId)
    ).length,
  }));
};

/**
 * Convert MUI DataGrid column definitions to Groups modal column configuration
 * Filters out group-related columns that shouldn't appear in the modal
 */
export const convertGridColumnsToGroupColumns = (
  gridColumns: GridColDef[]
): ColumnConfig[] => {
  return gridColumns
    .filter(col => col.field !== 'group_name') // Exclude group_name column from modal
    .map(col => ({
      field: col.field,
      headerName: col.headerName || col.field,
      getValue: col.valueGetter
        ? (row: Record<string, unknown>) =>
            col.valueGetter!({ row, field: col.field })
        : undefined,
      renderCell: col.renderCell
        ? (params: { value: unknown; row: Record<string, unknown> }) =>
            col.renderCell!({
              value: params.value,
              row: params.row,
              field: col.field,
              id: (params.row._id ?? params.row.id ?? '') as string,
              colDef: col,
              hasFocus: false,
              tabIndex: -1,
              cellMode: 'view',
            } as GridRenderCellParams)
        : undefined,
    }));
};

/**
 * Expand group IDs to visible member IDs
 * This function takes a group ID and returns all visible member row IDs
 * @param groupId - The group ID to expand (e.g., "group-123" or a regular row ID)
 * @param visibleRowIds - Set of currently visible row IDs
 * @param groups - Array of group definitions
 * @param displayData - Current display data (grouped format)
 * @param getRowId - Function to extract row ID from a row (defaults to getRowIdAsString)
 * @returns Array of visible member row IDs
 * @throws Error if group not found
 */
export const expandGroups = <
  T extends { id?: string; _id?: string; parentGroupId?: string },
>(
  groupId: string,
  visibleRowIds: Set<string>,
  groups: Group[],
  displayData: T[],
  getRowId: (row: T) => string = getRowIdAsString
): string[] => {
  // If not a group ID, return it as-is
  if (!groupId.startsWith('group-')) {
    return [groupId];
  }

  const actualGroupId = groupId.replace('group-', '');

  // Special handling for ungrouped items
  if (actualGroupId === 'ungrouped') {
    return displayData
      .filter(
        row =>
          row.parentGroupId === 'group-ungrouped' &&
          visibleRowIds.has(getRowId(row))
      )
      .map(row => getRowId(row));
  }

  // Find the group and return its visible member IDs
  const group = groups.find(g => g.id === actualGroupId);
  if (!group) {
    throw new Error(
      `Group not found: ${actualGroupId}. Available groups: ${groups.map(g => g.id).join(', ')}`
    );
  }
  return group.rowIds.filter(rowId => visibleRowIds.has(rowId));
};

/**
 * Hook to create an expandGroups function with current context
 * Wraps the expandGroups utility with groups and displayData from the calling hook
 *
 * @param groups - Array of group definitions
 * @param displayData - Current display data
 * @returns A function that expands group IDs to visible member IDs
 *
 * @example
 * ```tsx
 * const expandGroups = useExpandGroups(groups, displayData);
 * // Use in group checkbox handler
 * const handleCellClick = useGroupCheckboxHandler({
 *   expandGroups,
 *   // ...other props
 * });
 * ```
 */
export const useExpandGroups = <
  T extends { id?: string; _id?: string; parentGroupId?: string },
>(
  groups: Group[],
  displayData: T[]
) => {
  return useCallback(
    (groupId: string, visibleRowIds: Set<string>) => {
      return expandGroups(groupId, visibleRowIds, groups, displayData);
    },
    [groups, displayData]
  );
};
